import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, vShow as _vShow, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-center py-[6.4rem]"
}
const _hoisted_2 = { key: 1 }
const _hoisted_3 = {
  class: "profile-tabs",
  role: "tablist"
}
const _hoisted_4 = ["aria-selected", "onClick"]
const _hoisted_5 = { class: "mt-6" }
const _hoisted_6 = {
  key: 0,
  class: "order-3 lg:order-2 sm:mb-4 lg:mb-0"
}
const _hoisted_7 = { class: "bg-white backdrop-blur-[2px] h-full relative p-[1.2rem] sm:p-[1.2rem] rounded-[2.4rem] border border-white w-full min-h-[28rem] sm:min-h-[32rem] md:min-h-[29rem] mt-3 lg:mt-0" }
const _hoisted_8 = { class: "flex flex-col gap-[0.8rem]" }

import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { setSeo, jsonLd } from "@/composables/useSeo";

import LoaderOverlay from "@/components/loader/loader.vue";
import LayoutSection from "@/components/design/layoutSection.vue";
import MainContent from "@/components/design/mainContent.vue";
import SubContent from "@/components/design/subContent.vue";
import AppButton from "@/components/buttons/AppButton.vue";
import DoctorComponent from "@/components/doctor/doctorComponent.vue";
import DoctorSchedule from "@/components/search/doctorSchedule.vue";
import GoogleMaps from "@/components/doctor/googleMaps.vue";
import MapExpandButton from "@/components/buttons/MapExpandButton.vue";
import MapModal from "@/components/search/mapModal.vue";
import WorkExperience from "@/components/doctor/workExperience.vue";
import Services from "@/components/doctor/services.vue";
import ClassCardsList from "@/components/classes/ClassCardsList.vue";
import ClassJoinButton from "@/components/classes/ClassJoinButton.vue";
import { useProfile } from "@/composables";
import type { CalendarRequestPayload } from "@/types/domain";


export default /*@__PURE__*/_defineComponent({
  __name: 'ProfileView',
  setup(__props) {

const { t } = useI18n();
const mapRef = ref<typeof GoogleMaps | null>(null);

// Use the refactored composable
const {
  isMapModalVisible,
  mapDoctors,
  doctor,
  doctorSchedule,
  classes,
  isLoading,
  loadError,
  selectedBookingKey,
  hasDoctorSchedule,
  activeScheduleLoading,
  mapSelectedBookingKeys,
  aboutMeList,
  educationList,
  servicesList,
  showAboutSection,
  hasExperienceSection,
  hasServicesSection,
  hasMap,
  handleBookingOptionSelection,
  handleScheduleMoreDates,
  handleScheduleCalendar,
  viewMapModal,
  closeMapModal,
} = useProfile({
  translateFn: t,
});

// Single column with tabs: Booking (default) / Experience / Services / Sessions
const activeProfileTab = ref("reserva");
const profileTabs = computed(() => {
  const list: { id: string; label: string }[] = [
    { id: "reserva", label: t("Booking") },
  ];
  if (hasExperienceSection.value)
    list.push({ id: "experience", label: t("Experience") });
  if (hasServicesSection.value)
    list.push({ id: "services", label: t("services") });
  if (classes.value.length)
    list.push({ id: "sessions", label: t("Upcoming sessions") });
  return list;
});
watch(
  profileTabs,
  (list) => {
    if (!list.some((tab) => tab.id === activeProfileTab.value)) {
      activeProfileTab.value = list.length ? list[0].id : "";
    }
  },
  { immediate: true },
);

// Dynamic SEO — fires when the doctor profile finishes loading
watch(doctor, (d) => {
  if (!d?.user) return;
  const u = d.user;
  const name: string = u.name ?? u.fullName ?? "";
  const specialty: string = u.specialty ?? u.category ?? "";
  const aboutMeText: string = Array.isArray(u.aboutMe)
    ? u.aboutMe.filter(Boolean).join(" ")
    : typeof u.aboutMe === "string"
      ? u.aboutMe
      : "";
  const description: string =
    aboutMeText.trim() ||
    (specialty
      ? `${name}, especialista en ${specialty} en Medkus.`
      : `Perfil de ${name} en Medkus.`);
  setSeo({
    title: specialty ? `${name} · ${specialty}` : name,
    description: description.slice(0, 160),
    image: u.image || u.photo || undefined,
    type: "profile",
    jsonLd: jsonLd.medicalBusiness({
      name,
      description,
      image: u.image || u.photo || undefined,
      specialty,
      url: window.location.href,
    }),
  });
});

// Event handlers
function onScheduleBookingOptionChange(bookingKey: string) {
  handleBookingOptionSelection(bookingKey, { source: "schedule" });
}

function onMapBookingOptionSelection(doctor: any, bookingKey: string) {
  handleBookingOptionSelection(bookingKey, { source: "map" });
}

function onMapScheduleCalendar(doctor: any, payload: CalendarRequestPayload) {
  handleScheduleCalendar(payload);
}

function onMapScheduleMoreDates(doctor: any, payload: CalendarRequestPayload) {
  handleScheduleMoreDates(payload);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("main", null, [
    _createVNode(LoaderOverlay, { active: _unref(isLoading) }, null, 8, ["active"]),
    (!_unref(isLoading) && _unref(loadError))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(_ctx.$t("Content temporarily unavailable")), 1))
      : (!_unref(isLoading) && _unref(doctor))
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(LayoutSection, {
              maxWidthClass: 'max-w-[1100px] mx-auto',
              paddingClass: "pt-0 pb-10 sm:pb-12 md:pb-14 lg:pb-16",
              sectionClass: "mt-4"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_router_link, {
                  to: "/search/",
                  class: "profile-back mb-4"
                }, {
                  default: _withCtx(() => [
                    _cache[0] || (_cache[0] = _createElementVNode("svg", {
                      class: "profile-back__icon",
                      viewBox: "0 0 24 24",
                      "aria-hidden": "true"
                    }, [
                      _createElementVNode("path", {
                        d: "M15 18l-6-6 6-6",
                        fill: "none",
                        stroke: "currentColor",
                        "stroke-width": "2",
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round"
                      })
                    ], -1)),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t("Back to search")), 1)
                  ]),
                  _: 1
                }),
                _createElementVNode("div", _hoisted_3, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(profileTabs.value, (tab) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: tab.id,
                      type: "button",
                      role: "tab",
                      "aria-selected": activeProfileTab.value === tab.id,
                      class: _normalizeClass(["profile-tabs__tab", { 'profile-tabs__tab--active': activeProfileTab.value === tab.id }]),
                      onClick: ($event: any) => (activeProfileTab.value = tab.id)
                    }, _toDisplayString(tab.label), 11, _hoisted_4))
                  }), 128))
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _withDirectives(_createElementVNode("div", null, [
                    _createVNode(MainContent, { class: "space-y-6" }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", {
                          class: _normalizeClass(["grid grid-cols-1 gap-4", { 'lg:grid-cols-2': _unref(hasMap) }])
                        }, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["order-1 sm:mb-4 lg:mb-0", { 'lg:col-span-2': !_unref(hasMap) }])
                          }, [
                            _createVNode(SubContent, { paddingClass: "p-[1.6rem] h-full" }, {
                              default: _withCtx(() => [
                                _createVNode(DoctorComponent, {
                                  doctor: _unref(doctor).user,
                                  "show-about-me": false,
                                  "show-doctor-reviews": false,
                                  "active-booking-key": _unref(selectedBookingKey),
                                  onSelectBookingOption: _unref(handleBookingOptionSelection)
                                }, null, 8, ["doctor", "active-booking-key", "onSelectBookingOption"])
                              ]),
                              _: 1
                            })
                          ], 2),
                          (_unref(hasMap))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                _createElementVNode("div", _hoisted_7, [
                                  _createVNode(GoogleMaps, {
                                    doctors: _unref(mapDoctors).length ? _unref(mapDoctors) : [_unref(doctor).user],
                                    class: "w-full h-[28rem] sm:h-[32rem] rounded-[1.6rem] overflow-hidden",
                                    ref_key: "mapRef",
                                    ref: mapRef
                                  }, null, 8, ["doctors"]),
                                  _createVNode(MapExpandButton, {
                                    class: "absolute left-1/2 top-12 -translate-x-1/2",
                                    onClick: _unref(viewMapModal)
                                  }, null, 8, ["onClick"])
                                ])
                              ]))
                            : _createCommentVNode("", true),
                          (_unref(hasDoctorSchedule))
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 1,
                                class: _normalizeClass(["order-2 lg:order-3", { 'lg:col-span-2': _unref(hasMap) }])
                              }, [
                                _createVNode(SubContent, null, {
                                  default: _withCtx(() => [
                                    _createVNode(DoctorSchedule, {
                                      doctor: _unref(doctorSchedule),
                                      "selected-booking-key": _unref(selectedBookingKey) || undefined,
                                      "base-date": _unref(doctorSchedule)?.baseDateIso || undefined,
                                      "schedule-version": _unref(doctorSchedule)?.scheduleVersion || 0,
                                      onBookingOptionChange: onScheduleBookingOptionChange,
                                      onRequestMoreDates: _unref(handleScheduleMoreDates),
                                      onRequestCalendar: _unref(handleScheduleCalendar)
                                    }, null, 8, ["doctor", "selected-booking-key", "base-date", "schedule-version", "onRequestMoreDates", "onRequestCalendar"])
                                  ]),
                                  _: 1
                                })
                              ], 2))
                            : _createCommentVNode("", true)
                        ], 2)
                      ]),
                      _: 1
                    })
                  ], 512), [
                    [_vShow, activeProfileTab.value === 'reserva']
                  ]),
                  (activeProfileTab.value === 'experience')
                    ? (_openBlock(), _createBlock(MainContent, { key: 0 }, {
                        default: _withCtx(() => [
                          _createVNode(SubContent, null, {
                            default: _withCtx(() => [
                              _createVNode(WorkExperience, {
                                aboutMe: _unref(aboutMeList),
                                education: _unref(educationList),
                                "show-about-section": _unref(showAboutSection),
                                "show-title": false
                              }, null, 8, ["aboutMe", "education", "show-about-section"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (activeProfileTab.value === 'services')
                    ? (_openBlock(), _createBlock(MainContent, { key: 1 }, {
                        default: _withCtx(() => [
                          _createVNode(SubContent, null, {
                            default: _withCtx(() => [
                              _createVNode(Services, {
                                services: _unref(servicesList),
                                "show-title": false
                              }, null, 8, ["services"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (activeProfileTab.value === 'sessions')
                    ? (_openBlock(), _createBlock(MainContent, { key: 2 }, {
                        default: _withCtx(() => [
                          _createVNode(SubContent, null, {
                            default: _withCtx(() => [
                              _createVNode(ClassCardsList, {
                                items: _unref(classes),
                                title: '',
                                "show-collapse": false
                              }, {
                                cta: _withCtx(({ classData, navigate }) => [
                                  _createElementVNode("div", _hoisted_8, [
                                    _createVNode(ClassJoinButton, {
                                      "class-data": classData,
                                      label: _ctx.$t('Book now'),
                                      block: true
                                    }, null, 8, ["class-data", "label"]),
                                    _createVNode(AppButton, {
                                      label: _ctx.$t('More information'),
                                      variant: "secondary",
                                      class: "class-card__more-button",
                                      block: true,
                                      onClick: ($event: any) => (navigate())
                                    }, null, 8, ["label", "onClick"])
                                  ])
                                ]),
                                _: 1
                              }, 8, ["items"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 1
            }),
            (_unref(isMapModalVisible))
              ? (_openBlock(), _createBlock(MapModal, {
                  key: 0,
                  doctors: _unref(mapDoctors),
                  "dates-loading": _unref(activeScheduleLoading),
                  "selected-booking-keys": _unref(mapSelectedBookingKeys),
                  onClose: _unref(closeMapModal),
                  onSelectBookingOption: onMapBookingOptionSelection,
                  onRequestCalendar: onMapScheduleCalendar,
                  onRequestMoreDates: onMapScheduleMoreDates
                }, null, 8, ["doctors", "dates-loading", "selected-booking-keys", "onClose"]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
  ]))
}
}

})